import React from 'react';
import { DateRangePicker } from "@blueprintjs/datetime";
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

function createDateRangeShortcuts() {
  const today = new Date();
  // Matches Blueprint's default shortcut date math: subtract period, then add 1 day.
const yearsAgo = (years) =>
  moment(today).subtract(years, "years").add(1, "day").toDate();
  const monthsAgo = (months) => {
    const date = new Date(today);
    date.setMonth(date.getMonth() - months);
    date.setDate(date.getDate() + 1);
    return date;
  };
  const daysAgo = (days) => {
    const date = new Date(today);
    date.setDate(date.getDate() - days);
    return date;
  };
  // Forward ranges mirror the past ones: add the period, then take 1 day off.
  const daysAhead = (days) => moment(today).add(days, "days").toDate();
  const monthsAhead = (months) =>
    moment(today).add(months, "months").subtract(1, "day").toDate();
  const yearsAhead = (years) =>
    moment(today).add(years, "years").subtract(1, "day").toDate();

  return [
    { label: "Today", dateRange: [today, today] },
    { label: "Yesterday", dateRange: [daysAgo(1), daysAgo(1)] },
    { label: "Past week", dateRange: [daysAgo(6), today] },
    { label: "Past month", dateRange: [monthsAgo(1), today] },
    { label: "Past 3 months", dateRange: [monthsAgo(3), today] },
    { label: "Past 6 months", dateRange: [monthsAgo(6), today] },
    { label: "Past year", dateRange: [yearsAgo(1), today] },
    { label: "Past 2 years", dateRange: [yearsAgo(2), today] },
    { label: "Past 3 years", dateRange: [yearsAgo(3), today] },
    { label: "Past 5 years", dateRange: [yearsAgo(5), today] },
    { label: "Past 10 years", dateRange: [yearsAgo(10), today] },
    // Shown as a second column (see .bp3-daterangepicker-shortcuts in
    // styles/search-filters-bar.scss, which sets its row count to the 11
    // past ranges above).
    { label: "Tomorrow", dateRange: [daysAhead(1), daysAhead(1)] },
    { label: "Next week", dateRange: [today, daysAhead(6)] },
    { label: "Next month", dateRange: [today, monthsAhead(1)] },
    { label: "Next 3 months", dateRange: [today, monthsAhead(3)] },
    { label: "Next 6 months", dateRange: [today, monthsAhead(6)] },
    { label: "Next year", dateRange: [today, yearsAhead(1)] },
    { label: "Next 2 years", dateRange: [today, yearsAhead(2)] },
  ];
}

@observer
class SearchDateRangePicker extends React.Component {

  @observable startDate = null;
  @observable endDate = null;

  constructor(props) {
    super(props);
    this.startDate = this.props.startDate;
    this.endDate = this.props.endDate;
  }

  onDateRangeChange(range) {
    this.startDate = range[0];
    this.endDate = range[1];
    if (this.startDate && this.endDate) {
      this.props.onChange(range)
    }
  }

  render() {
    return (
      <DateRangePicker 
        value={[this.startDate, this.endDate]}
        formatDate={(d) => { return d.toDateString() }} 
        parseDate={(d) => { return new Date(d) }}
        allowSingleDayRange={true}
        onChange={this.onDateRangeChange.bind(this)}
        className="responsive-calender"
        maxDate={moment().add(25, 'years').toDate()}
        shortcuts={createDateRangeShortcuts()}
      />
    )
  }
}

SearchDateRangePicker.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
}

export default SearchDateRangePicker;
